import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scan, Plus, Warning } from "@phosphor-icons/react";
import QRScanner from "@/components/QRScanner";
import SantriSelect from "@/components/SantriSelect";
import BulkAttendance from "@/components/BulkAttendance";
import LaporanDivisi from "@/components/LaporanDivisi";
import { queueOp, flushQueue } from "@/lib/offlineSync";

const DIVISI_CONFIG = {
  keibadahan: {
    title: "Divisi Keibadahan",
    subtitle: "Absensi Ibadah + Pelanggaran",
    status: ["hadir", "izin", "alfa", "sakit", "berhalangan"],
    berhalangan_only_female: true,
  },
  lugho: {
    title: "Divisi Lugho (Bahasa)",
    subtitle: "Absensi Bahasa + Pelanggaran",
    status: ["aktif_berbahasa", "tidak_aktif_berbahasa", "izin", "alfa"],
    berhalangan_only_female: false,
  },
  kedisiplinan: {
    title: "Divisi Kedisiplinan",
    subtitle: "Absensi Kegiatan + Pelanggaran",
    status: ["hadir", "izin", "alfa", "sakit"],
    berhalangan_only_female: false,
  },
  kebersihan: {
    title: "Divisi Kebersihan",
    subtitle: "Absensi Piket + Pelanggaran Kebersihan",
    status: ["hadir", "izin", "alfa", "sakit"],
    berhalangan_only_female: false,
  },
  keamanan: {
    title: "Divisi Keamanan",
    subtitle: "Absensi Piket Jaga + Pelanggaran Keamanan",
    status: ["hadir", "izin", "alfa", "sakit"],
    berhalangan_only_female: false,
  },
  kesehatan: {
    title: "Divisi Kesehatan",
    subtitle: "Cek Kesehatan Harian + Pelanggaran Kesehatan",
    status: ["sehat", "sakit", "izin_berobat", "alfa"],
    berhalangan_only_female: false,
  },
  humas: {
    title: "Divisi Humas",
    subtitle: "Perizinan & Penjemputan + Pelanggaran Humas",
    status: ["hadir", "izin", "alfa", "sakit"],
    berhalangan_only_female: false,
    hide_absensi: true,  // Humas uses Perizinan module instead of absensi kegiatan
  },
  logistik: {
    title: "Divisi Logistik",
    subtitle: "Absensi Makan + Pelanggaran Logistik",
    status: ["hadir", "izin", "alfa", "sakit"],
    berhalangan_only_female: false,
    hide_absensi: true,  // Logistik uses Makan module instead
  },
};

export default function DivisiPage() {
  const { divisi } = useParams();
  const cfg = DIVISI_CONFIG[divisi];
  const [tab, setTab] = useState("massal");
  const [kegiatans, setKegiatans] = useState([]);
  const [absensiItems, setAbsensiItems] = useState([]);
  const [pelItems, setPelItems] = useState([]);
  const [pelTypes, setPelTypes] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [form, setForm] = useState({
    santri: null,
    nis: "",
    kegiatan: "",
    tanggal: new Date().toISOString().slice(0, 10),
    jam: "",
    status: cfg?.status[0] || "hadir",
    alasan: "",
  });
  const [pelForm, setPelForm] = useState({
    santri: null, nis: "", jenis_id: "", jenis_manual: "", kategori: "", poin: "",
    tanggal: new Date().toISOString().slice(0, 10), hari: "", jam: "", catatan: "",
  });
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    setTab("massal");
    setForm((f) => ({ ...f, kegiatan: "", nis: "", santri: null, status: cfg?.status[0] || "hadir", alasan: "" }));
    (async () => {
      try {
        const all = await api.get("/master/kegiatan");
        setKegiatans(all.data.filter((k) => (k.divisi || "").toLowerCase() === divisi));
        const p = await api.get("/master/pelanggaran");
        setPelTypes(p.data.filter((x) => (x.divisi || "").toLowerCase() === divisi));
      } catch (e) { /* ignore */ }
    })();
  }, [divisi]);  // eslint-disable-line

  const loadAbsensi = async () => {
    try {
      const { data } = await api.get("/absensi-divisi", { params: { divisi, tanggal: dateFilter } });
      setAbsensiItems(data);
    } catch (e) { /* ignore */ }
  };
  const loadPelanggaran = async () => {
    try {
      const { data } = await api.get("/pelanggaran-divisi", { params: { divisi } });
      setPelItems(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (tab === "absensi") loadAbsensi(); }, [tab, dateFilter, divisi]);  // eslint-disable-line
  useEffect(() => { if (tab === "pelanggaran") loadPelanggaran(); }, [tab, divisi]);  // eslint-disable-line

  if (!cfg) return <div className="text-slate-500">Divisi tidak dikenal.</div>;

  const submitAbsensi = async (e) => {
    e?.preventDefault?.();
    const nis = form.santri?.nis || form.nis;
    if (!nis || !form.kegiatan) { toast.error("Santri dan kegiatan wajib"); return; }
    const payload = { nis, kegiatan: form.kegiatan, tanggal: form.tanggal, jam: form.jam, status: form.status, alasan: form.alasan, divisi };
    try {
      if (navigator.onLine) {
        await api.post("/absensi-divisi", payload);
        toast.success("Absensi tercatat");
      } else {
        await queueOp({ op: "create", path: "/absensi-divisi", payload });
        toast.success("Offline — antre untuk sync");
      }
      setForm({ ...form, santri: null, nis: "", alasan: "" });
      loadAbsensi();
      flushQueue(api);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const onScanned = async (text) => {
    setScanOpen(false);
    try {
      const s = await api.get(`/santri/by-nis/${encodeURIComponent(text)}`);
      setForm((f) => ({ ...f, santri: { nis: s.data.nis, nama: s.data.nama, kamar_nama: s.data.kamar_nama, kelas_nama: s.data.kelas_nama, id: s.data.id }, nis: s.data.nis }));
      setTimeout(() => submitAbsensi(), 150);
    } catch (_) { toast.error("Santri tidak ditemukan"); }
  };

  const submitPelanggaran = async (e) => {
    e.preventDefault();
    const nis = pelForm.santri?.nis || pelForm.nis;
    if (!nis) { toast.error("Santri wajib dipilih"); return; }
    const payload = { nis, divisi, jenis_id: pelForm.jenis_id, jenis_manual: pelForm.jenis_manual, kategori: pelForm.kategori, poin: pelForm.poin ? Number(pelForm.poin) : undefined, tanggal: pelForm.tanggal, hari: pelForm.hari, jam: pelForm.jam, catatan: pelForm.catatan };
    if (!payload.jenis_id) delete payload.jenis_id;
    try {
      await api.post("/pelanggaran-divisi", payload);
      toast.success("Pelanggaran dicatat");
      setPelForm({ ...pelForm, santri: null, nis: "", jenis_manual: "", kategori: "", poin: "", catatan: "" });
      loadPelanggaran();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Modul</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">{cfg.title}</h1>
        <p className="text-sm text-slate-500 mt-1">{cfg.subtitle}</p>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        {!cfg.hide_absensi && (
          <>
            <button data-testid="tab-massal" onClick={() => setTab("massal")} className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "massal" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Absensi Massal</button>
            <button data-testid="tab-absensi" onClick={() => setTab("absensi")} className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "absensi" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Absensi Individu / QR</button>
          </>
        )}
        <button data-testid="tab-pelanggaran" onClick={() => setTab("pelanggaran")} className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "pelanggaran" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Pelanggaran</button>
        <button data-testid="tab-laporan" onClick={() => setTab("laporan")} className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "laporan" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Laporan Divisi</button>
      </div>

      {tab === "laporan" && (
        <LaporanDivisi
          divisiKey={divisi}
          modules={cfg.hide_absensi
            ? [{ key: `${divisi}_pelanggaran`, label: `${cfg.title} — Pelanggaran` }]
            : [
                { key: `${divisi}_absensi`, label: `${cfg.title} — Absensi` },
                { key: `${divisi}_pelanggaran`, label: `${cfg.title} — Pelanggaran` },
              ]}
        />
      )}

      {tab === "massal" && (
        <div className="space-y-4">
          <div className="imsyaf-card p-5 grid md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><label className="text-xs">Kegiatan *</label>
              <select className="imsyaf-input" value={form.kegiatan} onChange={(e) => setForm({ ...form, kegiatan: e.target.value })} data-testid="massal-kegiatan">
                <option value="">Pilih kegiatan…</option>
                {kegiatans.map((k) => <option key={k.id} value={k.nama}>{k.nama}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
            <div><label className="text-xs">Jam</label>
              <input type="time" className="imsyaf-input" value={form.jam} onChange={(e) => setForm({ ...form, jam: e.target.value })} /></div>
          </div>
          {!form.kegiatan ? (
            <div className="imsyaf-card p-8 text-center text-slate-400">Pilih kegiatan terlebih dahulu untuk memuat daftar santri.</div>
          ) : (
            <BulkAttendance
              endpoint="/absensi-divisi"
              basePayload={{ divisi, kegiatan: form.kegiatan, tanggal: form.tanggal, jam: form.jam }}
              statuses={cfg.status}
              izinKey={divisi === "kesehatan" ? "izin_berobat" : "izin"}
              femaleOnlyStatuses={["berhalangan"]}
              onDone={loadAbsensi}
            />
          )}
        </div>
      )}

      {tab === "absensi" && (
        <>
          <form onSubmit={submitAbsensi} className="imsyaf-card p-5 grid md:grid-cols-6 gap-3">
            <div className="md:col-span-3"><label className="text-xs">Santri (cari nama / kamar) *</label>
              <SantriSelect value={form.santri} onChange={(s) => setForm({ ...form, santri: s, nis: s?.nis || "" })} testid="div-santri" /></div>
            <div className="md:col-span-2"><label className="text-xs">Kegiatan *</label>
              <select required className="imsyaf-input" value={form.kegiatan} onChange={(e) => setForm({ ...form, kegiatan: e.target.value })} data-testid="div-kegiatan">
                <option value="">Pilih…</option>
                {kegiatans.map((k) => <option key={k.id} value={k.nama}>{k.nama}</option>)}
              </select></div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
            <div><label className="text-xs">Jam</label>
              <input type="time" className="imsyaf-input" value={form.jam} onChange={(e) => setForm({ ...form, jam: e.target.value })} /></div>
            <div className="md:col-span-2"><label className="text-xs">Status</label>
              <select className="imsyaf-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="div-status">
                {cfg.status.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select></div>
            {form.status === "izin" && (
              <div className="md:col-span-6"><label className="text-xs">Alasan Izin</label>
                <input className="imsyaf-input" value={form.alasan} onChange={(e) => setForm({ ...form, alasan: e.target.value })} data-testid="div-alasan" /></div>
            )}
            <div className="md:col-span-6 flex flex-wrap gap-2">
              <button type="submit" className="imsyaf-btn-primary" data-testid="div-save"><Plus size={16}/> Simpan Absensi</button>
              <button type="button" onClick={() => setScanOpen(true)} className="imsyaf-btn-outline" data-testid="div-scan"><Scan size={16}/> Scan QR/Barcode</button>
            </div>
          </form>

          {scanOpen && (
            <div className="imsyaf-card p-5">
              <QRScanner onScan={onScanned} onError={(m) => { setScanOpen(false); toast.error(m); }} />
              <div className="text-right mt-3"><button onClick={() => setScanOpen(false)} className="imsyaf-btn-outline">Tutup</button></div>
            </div>
          )}

          <div className="imsyaf-card p-4 flex items-end gap-3">
            <div><label className="text-xs">Filter Tanggal</label>
              <input type="date" className="imsyaf-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} /></div>
            <div className="text-sm text-slate-500 pb-2">Total: <b>{absensiItems.length}</b></div>
          </div>

          <div className="imsyaf-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm zebra">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left px-4 py-2">Waktu</th><th className="text-left px-4 py-2">NIS</th>
                  <th className="text-left px-4 py-2">Nama</th><th className="text-left px-4 py-2">Kegiatan</th>
                  <th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Petugas</th>
                </tr></thead>
                <tbody>{absensiItems.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs">{a.created_at?.slice(11, 16)}</td>
                    <td className="px-4 py-2 font-mono">{a.santri_nis}</td>
                    <td className="px-4 py-2">{a.santri_nama}</td>
                    <td className="px-4 py-2">{a.kegiatan}</td>
                    <td className="px-4 py-2"><span className={`imsyaf-badge ${a.status === "hadir" || a.status === "aktif_berbahasa" ? "bg-green-100 text-green-800" : ["alfa","tidak_aktif_berbahasa"].includes(a.status) ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{a.status.replace(/_/g, " ")}</span></td>
                    <td className="px-4 py-2 text-xs text-slate-500">{a.petugas_nama}</td>
                  </tr>
                ))}
                {absensiItems.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Belum ada data.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "pelanggaran" && (
        <>
          <form onSubmit={submitPelanggaran} className="imsyaf-card p-5 grid md:grid-cols-6 gap-3">
            <div className="md:col-span-3"><label className="text-xs">Santri (cari nama / kamar) *</label>
              <SantriSelect value={pelForm.santri} onChange={(s) => setPelForm({ ...pelForm, santri: s, nis: s?.nis || "" })} testid="pel-div-santri" /></div>
            <div className="md:col-span-3"><label className="text-xs">Pilih dari Master</label>
              <select className="imsyaf-input" value={pelForm.jenis_id} onChange={(e) => {
                const j = pelTypes.find((t) => t.id === e.target.value);
                setPelForm({ ...pelForm, jenis_id: e.target.value, jenis_manual: j?.nama || "", kategori: j?.kategori || "", poin: j?.poin ?? "" });
              }} data-testid="pel-div-jenis">
                <option value="">— Kustom / Manual —</option>
                {pelTypes.map((t) => <option key={t.id} value={t.id}>{t.nama} ({t.poin}p, {t.kategori || t.divisi})</option>)}
              </select></div>
            <div className="md:col-span-3"><label className="text-xs">Pelanggaran (jika manual)</label>
              <input className="imsyaf-input" value={pelForm.jenis_manual} onChange={(e) => setPelForm({ ...pelForm, jenis_manual: e.target.value })} data-testid="pel-div-nama" /></div>
            <div><label className="text-xs">Kategori</label>
              <input className="imsyaf-input" value={pelForm.kategori} onChange={(e) => setPelForm({ ...pelForm, kategori: e.target.value })} /></div>
            <div><label className="text-xs">Poin</label>
              <input type="number" className="imsyaf-input" value={pelForm.poin} onChange={(e) => setPelForm({ ...pelForm, poin: e.target.value })} data-testid="pel-div-poin" /></div>
            <div><label className="text-xs">Hari</label><input className="imsyaf-input" value={pelForm.hari} onChange={(e) => setPelForm({ ...pelForm, hari: e.target.value })} placeholder="Senin/Selasa/…" /></div>
            <div><label className="text-xs">Tanggal</label><input type="date" className="imsyaf-input" value={pelForm.tanggal} onChange={(e) => setPelForm({ ...pelForm, tanggal: e.target.value })} /></div>
            <div><label className="text-xs">Jam</label><input type="time" className="imsyaf-input" value={pelForm.jam} onChange={(e) => setPelForm({ ...pelForm, jam: e.target.value })} /></div>
            <div className="md:col-span-6"><label className="text-xs">Catatan</label><input className="imsyaf-input" value={pelForm.catatan} onChange={(e) => setPelForm({ ...pelForm, catatan: e.target.value })} /></div>
            <div className="md:col-span-6"><button className="imsyaf-btn-primary" data-testid="pel-div-save"><Warning size={16}/> Simpan Pelanggaran</button></div>
          </form>

          <div className="imsyaf-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm zebra">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Santri</th>
                  <th className="text-left px-4 py-2">Pelanggaran</th><th className="text-left px-4 py-2">Kategori</th>
                  <th className="text-left px-4 py-2">Poin</th><th className="text-left px-4 py-2">Jam</th>
                  <th className="text-left px-4 py-2">Pencatat</th><th className="text-left px-4 py-2">Catatan</th>
                </tr></thead>
                <tbody>{pelItems.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-2">{p.tanggal}</td>
                    <td className="px-4 py-2"><div className="font-medium">{p.santri_nama}</div><div className="text-xs font-mono text-slate-500">{p.santri_nis}</div></td>
                    <td className="px-4 py-2">{p.jenis_nama}</td>
                    <td className="px-4 py-2"><span className="imsyaf-badge bg-slate-100">{p.kategori || "-"}</span></td>
                    <td className="px-4 py-2 font-bold text-red-700">{p.poin}</td>
                    <td className="px-4 py-2">{p.jam || "-"}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{p.pencatat_nama}</td>
                    <td className="px-4 py-2 text-slate-500">{p.catatan || "-"}</td>
                  </tr>
                ))}
                {pelItems.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Belum ada pelanggaran.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
